/*
  ICONTACTIC W-176 / W-177 / W-180 / W-181 / W-182 / W-186 — the headline price row, the option pickers, the
  quantity and the trust strips.

  W-176: the headline price and the instalment line follow the selected bundle tier and variant.

  The buy column shows one headline price (product-price > .ict-price > .price) and, under it, the buy-now-pay-
  later line ("4 interest-free payments of about $37.50"). Further down sit the Bundle & save tiers. Picking a
  tier changed the quantity in the form and the totals inside the tier rows, but the headline stayed at the single
  unit price and the instalment stayed at a quarter of it, so a shopper who picked "4 units" still read $149.99 and
  $37.50 at the top.

  Both are now derived from the numbers the tier block already exposes on <ict-bundle>: the unit price of the
  selected variant (data-unit, kept current by the block on variant:update) and the quantity / percentage of each
  tier. Tier 1 shows the unit price as before. Any discounted tier shows the discounted total with the full price
  struck through and the quantity, and the instalment is that total divided by the number of payments the line
  already states. So the numbers at the top always match what "Add to cart" will add.

  W-177: the stock line ("In stock · 50 ready to ship from Florida") sits to the right of the price instead of
  below the bundle tiers, so availability is read at the same glance as the price. The stock block is a sibling
  of the buy buttons in the template; it is moved next to the price at runtime and both are wrapped in a flex
  row (.ict-price-row, styled in icontactic-pdp-tweaks.css). Moving it in the template would mean editing the
  47 KB product.json and restructuring the identity group; a runtime move keeps the template untouched.

  W-180: the Color option is drawn as small colour squares (icontactic-pdp-tweaks.css) instead of the theme's
  text buttons. The product option values carry no Shopify swatch data, so the colour of each square is resolved
  here from the value's name (Black, Coyote Brown, Ranger Green, Wolf Grey, Tan Brown, Grid White, Black Camo,
  Grey, White...) and written as --ict-swatch on the button's text span. Only the option named Color / Colour is
  treated this way, and only its values that resolve to a colour; other options (Size, Mount, Configuration)
  keep the theme's text buttons, and an unknown colour name keeps its text so nothing is ever hidden. The name
  of the selected colour is written into the legend ("Color: Black") since it is no longer on the button, and
  the selected label carries a class (is-ict-selected) rather than relying on :checked in CSS: after the theme
  morphs the picker on a variant change, Chrome kept the old radio's :checked styling until a full restyle.

  W-181: the quantity stepper sits on the row of the last option (to the right of the colour swatches, or of
  the last compact option when there is one) instead of on its own row above the Add to cart button. The
  stepper is part of the product form in the template; it is moved out at runtime and its input is tied back
  to the form with the form attribute, so the quantity is still posted with Add to cart. The stepper's own +/-
  handlers live on the stepper element and keep working wherever it sits; the bundle block finds the input by
  name within the section. The variant picker only ever morphs itself on a variant change, so the rows are
  never torn down.

  W-182: compact rendering of the non-colour options. Options with up to three values become a row of small
  pills; options with more become a dropdown; each carries the same "LABEL: value" header as the colour
  swatches. The dropdown is a listbox of our own (button + list) rather than a native <select>: the native
  popup is painted by the operating system (white panel, system-blue highlight) and cannot be styled to the
  theme. It closes on outside click and Escape, and takes arrow keys / Enter / Space. The compact controls
  hold no state of their own: a pill click or a list choice clicks the matching radio in the theme's picker,
  so variant selection, price and URL keep flowing through the theme, and the compact block re-reads the
  radios after every re-render. The block is rebuilt only when the set of options changes (a combined-listing
  product swap); otherwise only selected states are touched.

  COMPACT_MODE below keeps the theme's original text buttons available: 'replace' (current) hides them and
  shows only the compact controls; 'compare' shows both, the compact block under the original with a note;
  'off' removes the compact block and leaves the theme's buttons as they ship. Nothing is deleted in any mode:
  the original fieldsets stay in the DOM (they are what the compact controls drive).

  W-186: the two trust strips (grey and amber marquees, template sections strip_a and strip_b) appear twice:
  where the template puts them, under the buy area, and again under "Where it goes". "Where it goes" is a
  block inside the landing section, and a section cannot be placed inside another section from the template,
  so a copy of each strip section is made at runtime and placed in the landing section right after that
  block, inside one wrapper (.ict-strips) that spans the full page width. The copy is reset to the markup the
  server sent (one item set, no duplicate track) before it is inserted, so the marquee component builds its
  loop for the copy the same way it did for the original; the copy's id gets a -copy suffix so no id repeats.

  The theme re-renders these blocks when the variant changes, which wipes whatever was written into them and can
  put the stock line back where the template has it, so everything is re-checked and re-applied after each
  re-render: a mutation observer on the buy column schedules a pass (at most one per task). Every writer in
  that pass compares before it writes, so a pass that changes nothing produces no mutation and the observer
  stays quiet. (The price and instalment writers did not compare at first, and the observer re-ran the pass
  hundreds of times a second.) Loaded from snippets/icontactic-zoom-pointer.liquid.
*/
(function () {
  /* W-182: 'replace' | 'compare' | 'off' — see the header comment. */
  var COMPACT_MODE = 'replace';

  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* W-180: colour of a swatch from the option value's name. Exact names first, then the last colour word. */
  var SWATCH_EXACT = {
    'black': '#131517',
    'black camo': '#2b2f2b',
    'green': '#4f5c45',
    'ranger green': '#4a5541',
    'olive': '#5b6142',
    'coyote brown': '#8a6a45',
    'coyote': '#8a6a45',
    'tan brown': '#a58a64',
    'tan': '#b89b74',
    'wolf grey': '#6e7275',
    'wolf gray': '#6e7275',
    'grey': '#7b7f83',
    'gray': '#7b7f83',
    'white': '#e8e8e6',
    'grid white': '#e8e8e6',
    'navy': '#1f2a3d',
    'blue': '#2f4a6e',
    'red': '#8c2b2b',
    'orange': '#c8681e',
    'brown': '#5e4430',
    'sand': '#c9b48e',
    'khaki': '#b9a77a',
    'multicam': '#6b6a4a'
  };

  function swatchColor(name) {
    var key = String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (SWATCH_EXACT[key]) return SWATCH_EXACT[key];
    var words = key.split(' ');
    for (var i = words.length - 1; i >= 0; i--) {
      if (SWATCH_EXACT[words[i]]) return SWATCH_EXACT[words[i]];
    }
    return '';
  }

  function legendName(fs) {
    var legend = fs.querySelector('legend');
    if (!legend) return '';
    var text = '';
    legend.childNodes.forEach(function (n) {
      if (n.nodeType === 3) text += n.textContent;
    });
    return text.trim();
  }

  function isColorOption(fs) {
    return /^colou?r$/i.test(legendName(fs));
  }

  function setClass(el, name, on) {
    if (el.classList.contains(name) !== on) el.classList.toggle(name, on);
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  /*
    W-180: colour squares, selected class and legend value on the Color option. Only writes when something
    actually changes, so the mutation observer below does not feed back into itself.
  */
  function syncColorOption(section) {
    section.querySelectorAll('fieldset.variant-option--buttons:not(.variant-option--swatches)').forEach(function (fs) {
      if (!isColorOption(fs)) return;
      var legend = fs.querySelector('legend');
      var checked = fs.querySelector('input[type="radio"]:checked');

      var allResolved = true;
      var labels = fs.querySelectorAll('.variant-option__button-label');
      labels.forEach(function (label) {
        var input = label.querySelector('input[type="radio"]');
        var span = label.querySelector('.variant-option__button-label__text');
        if (!input || !span) return;
        var color = swatchColor(input.value);
        if (!color) allResolved = false;
        if (span.style.getPropertyValue('--ict-swatch') !== color) span.style.setProperty('--ict-swatch', color);
        setClass(label, 'is-ict-selected', !!checked && label.contains(checked));
      });

      /* Squares only when every value has a colour; otherwise the theme's text buttons stay. */
      setClass(fs, 'ict-swatch-set', allResolved);

      if (!checked) return;
      var value = legend.querySelector('.ict-option-value');
      if (!value) {
        value = document.createElement('span');
        value.className = 'ict-option-value';
        legend.appendChild(value);
      }
      setText(value, checked.value);
    });
  }

  /* W-182: compact rendering of the non-colour options. */
  var COMPACT_PILL_MAX = 3;

  function compactFieldsets(section) {
    var picker = section.querySelector('.product-details variant-picker');
    if (!picker) return [];
    return Array.prototype.filter.call(picker.querySelectorAll('fieldset.variant-option--buttons'), function (fs) {
      return !isColorOption(fs) && fs.querySelectorAll('input[type="radio"]').length > 1;
    });
  }

  function compactSignature(fieldsets) {
    return fieldsets.map(function (fs) {
      var values = Array.prototype.map.call(fs.querySelectorAll('input[type="radio"]'), function (i) { return i.value; });
      return legendName(fs) + '=' + values.join('|');
    }).join(';;');
  }

  function radioFor(fs, value) {
    return Array.prototype.find.call(fs.querySelectorAll('input[type="radio"]'), function (i) { return i.value === value; }) || null;
  }

  /* One document listener closes any open listbox on an outside click or Escape. */
  var CARET = '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHECK = '<svg aria-hidden="true" class="ict-dd__check" viewBox="0 0 12 12"><path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function closeAllDropdowns(except) {
    document.querySelectorAll('.ict-dd.is-open').forEach(function (dd) {
      if (dd !== except) setDropdownOpen(dd, false);
    });
  }

  function setDropdownOpen(dd, open) {
    setClass(dd, 'is-open', open);
    var btn = dd.querySelector('.ict-dd__btn');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var list = dd.querySelector('.ict-dd__list');
    if (list) list.hidden = !open;
    if (open && list) {
      var on = list.querySelector('.ict-dd__opt.is-on') || list.querySelector('.ict-dd__opt');
      if (on) on.focus();
    }
  }

  var docListenersBound = false;
  function bindDocListeners() {
    if (docListenersBound) return;
    docListenersBound = true;
    document.addEventListener('click', function (e) {
      var inside = e.target.closest && e.target.closest('.ict-dd');
      closeAllDropdowns(inside);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllDropdowns(null);
    });
  }

  function buildDropdown(section, idx, radios, name) {
    var dd = document.createElement('div');
    dd.className = 'ict-dd ict-compact-select';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ict-dd__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', name);
    btn.innerHTML = '<span class="ict-dd__val"></span>' + CARET;
    dd.appendChild(btn);

    var list = document.createElement('ul');
    list.className = 'ict-dd__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    radios.forEach(function (radio) {
      var li = document.createElement('li');
      li.className = 'ict-dd__opt';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.tabIndex = -1;
      li.dataset.value = radio.value;
      li.innerHTML = '<span class="ict-dd__text"></span>' + CHECK;
      li.querySelector('.ict-dd__text').textContent = radio.value;
      if (radio.getAttribute('data-option-available') === 'false') li.classList.add('is-unavailable');
      li.addEventListener('click', function () {
        var live = compactFieldsets(section)[idx];
        var r = live && radioFor(live, radio.value);
        if (r && !r.checked) r.click();
        setDropdownOpen(dd, false);
        btn.focus();
      });
      list.appendChild(li);
    });
    dd.appendChild(list);

    btn.addEventListener('click', function () {
      var open = !dd.classList.contains('is-open');
      closeAllDropdowns(dd);
      setDropdownOpen(dd, open);
    });

    dd.addEventListener('keydown', function (e) {
      var opts = Array.prototype.slice.call(list.querySelectorAll('.ict-dd__opt'));
      var i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!dd.classList.contains('is-open')) setDropdownOpen(dd, true);
        var next = e.key === 'ArrowDown' ? Math.min(i + 1, opts.length - 1) : Math.max(i - 1, 0);
        if (i < 0) next = e.key === 'ArrowDown' ? 0 : opts.length - 1;
        if (opts[next]) opts[next].focus();
      } else if ((e.key === 'Enter' || e.key === ' ') && i >= 0) {
        e.preventDefault();
        opts[i].click();
      } else if (e.key === 'Tab') {
        setDropdownOpen(dd, false);
      }
    });

    bindDocListeners();
    return dd;
  }

  function buildCompact(section, fieldsets) {
    var block = document.createElement('div');
    block.className = 'ict-compact-options';

    var note = document.createElement('p');
    note.className = 'ict-compact-options__note';
    note.textContent = 'Vista compacta (prueba)';
    block.appendChild(note);

    fieldsets.forEach(function (fs, idx) {
      var radios = fs.querySelectorAll('input[type="radio"]');
      var wrap = document.createElement('div');
      wrap.className = 'ict-compact-option';
      wrap.dataset.ictIndex = String(idx);

      var head = document.createElement('div');
      head.className = 'ict-compact-option__label';
      head.textContent = legendName(fs);
      var val = document.createElement('span');
      val.className = 'ict-option-value';
      head.appendChild(val);
      wrap.appendChild(head);

      /* The control row also hosts the quantity stepper when this is the last option (W-181). */
      var row = document.createElement('div');
      row.className = 'ict-compact-option__row';
      wrap.appendChild(row);

      if (radios.length <= COMPACT_PILL_MAX) {
        var pills = document.createElement('div');
        pills.className = 'ict-compact-pills';
        radios.forEach(function (radio) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ict-compact-pill';
          b.textContent = radio.value;
          b.dataset.value = radio.value;
          if (radio.getAttribute('data-option-available') === 'false') b.classList.add('is-unavailable');
          b.addEventListener('click', function () {
            var live = compactFieldsets(section)[idx];
            var r = live && radioFor(live, radio.value);
            if (r && !r.checked) r.click();
          });
          pills.appendChild(b);
        });
        row.appendChild(pills);
      } else {
        row.appendChild(buildDropdown(section, idx, radios, legendName(fs)));
      }
      block.appendChild(wrap);
    });
    return block;
  }

  function syncCompactOptions(section) {
    var fieldsets = compactFieldsets(section);
    var picker = section.querySelector('.product-details variant-picker');
    var existing = section.querySelector('.ict-compact-options');

    var teardown = function () {
      if (!existing) return;
      /* The stepper may live inside the block (W-181); park it before the block goes. */
      var qty = existing.querySelector('quantity-selector-component');
      if (qty) existing.parentNode.insertBefore(qty, existing);
      existing.remove();
      existing = null;
    };

    if (COMPACT_MODE === 'off' || !picker || !fieldsets.length) {
      teardown();
      fieldsets.forEach(function (fs) { setClass(fs, 'ict-compact-hidden', false); });
      return;
    }

    var sig = compactSignature(fieldsets);
    var anchor = picker.parentElement.classList.contains('ict-color-qty-row') ? picker.parentElement : picker;
    if (!existing || existing.dataset.ictSig !== sig) {
      teardown();
      existing = buildCompact(section, fieldsets);
      existing.dataset.ictSig = sig;
      anchor.parentNode.insertBefore(existing, anchor.nextSibling);
    } else if (existing.previousElementSibling !== anchor) {
      anchor.parentNode.insertBefore(existing, anchor.nextSibling);
    }

    var live = COMPACT_MODE === 'replace';
    setClass(existing, 'is-live', live);
    fieldsets.forEach(function (fs) { setClass(fs, 'ict-compact-hidden', live); });

    /* Selected states follow the theme's radios; only written when they differ. */
    fieldsets.forEach(function (fs, idx) {
      var wrap = existing.querySelector('.ict-compact-option[data-ict-index="' + idx + '"]');
      if (!wrap) return;
      var checked = fs.querySelector('input[type="radio"]:checked');
      var value = checked ? checked.value : '';
      setText(wrap.querySelector('.ict-option-value'), value);
      wrap.querySelectorAll('.ict-compact-pill').forEach(function (b) {
        setClass(b, 'is-on', b.dataset.value === value);
      });
      setText(wrap.querySelector('.ict-dd__val'), value);
      wrap.querySelectorAll('.ict-dd__opt').forEach(function (li) {
        var on = li.dataset.value === value;
        setClass(li, 'is-on', on);
        var aria = on ? 'true' : 'false';
        if (li.getAttribute('aria-selected') !== aria) li.setAttribute('aria-selected', aria);
      });
    });
  }

  /* W-181: quantity stepper on the last option row, still posted with the product form. Idempotent. */
  var formSeq = 0;
  function placeQuantity(section) {
    var picker = section.querySelector('.product-details variant-picker');
    var qty = section.querySelector('.product-details quantity-selector-component');
    if (!picker || !qty) return;

    var row = picker.parentElement;
    if (!row || !row.classList.contains('ict-color-qty-row')) {
      row = document.createElement('div');
      row.className = 'ict-color-qty-row';
      picker.parentNode.insertBefore(row, picker);
      row.appendChild(picker);
    }

    /* Beside the last compact option when there is one, otherwise beside the colour swatches. */
    var compact = section.querySelector('.ict-compact-options');
    var target = compact ? compact.querySelector('.ict-compact-option:last-of-type > .ict-compact-option__row') : null;
    if (!target) target = row;
    if (qty.parentElement === target) return;

    var form = qty.closest('form');
    var input = qty.querySelector('input[name="quantity"]');
    if (form && input) {
      /* form.id is shadowed by the form's own <input name="id">, so the attribute is read directly. */
      var id = form.getAttribute('id');
      if (!id) {
        id = 'ict-product-form-' + (++formSeq);
        form.setAttribute('id', id);
      }
      input.setAttribute('form', id);
    }
    target.appendChild(qty);
  }

  /*
    W-186: a copy of the two trust strips under "Where it goes", inside the landing section. Runs once. The
    marquee component may already have built its loop on the original (repeated item sets inside the content
    track plus a duplicate track); the copy is reset to one item set and no duplicate before it is inserted so
    the component builds the copy's loop from scratch, and the copy's ids are suffixed so none repeat.
  */
  function resetMarquee(m) {
    var content = m.querySelector('[ref="content"]');
    var wrapper = m.querySelector('[ref="wrapper"]');
    if (!content || !wrapper) return;
    while (content.children.length > 1) content.lastElementChild.remove();
    Array.prototype.forEach.call(wrapper.children, function (child) {
      if (child !== content) child.remove();
    });
    m.style.removeProperty('--marquee-speed');
  }

  function placeStrips() {
    if (document.querySelector('.ict-strips')) return;
    var placements = document.querySelector('.ict-landing > .ict-landing__placements');
    if (!placements) return;
    var strips = document.querySelectorAll('.shopify-section[id$="__strip_a"], .shopify-section[id$="__strip_b"]');
    if (!strips.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'ict-strips';
    strips.forEach(function (s) {
      var copy = s.cloneNode(true);
      copy.id = s.id + '-copy';
      copy.querySelectorAll('[id]').forEach(function (el) { el.id = el.id + '-copy'; });
      copy.querySelectorAll('marquee-component').forEach(resetMarquee);
      copy.removeAttribute('data-shopify-editor-section');
      wrap.appendChild(copy);
    });
    placements.parentNode.insertBefore(wrap, placements.nextSibling);
  }

  /* One pass per task at most, however many events or mutations ask for it. */
  function scheduler(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        fn();
      }, 0);
    };
  }

  function init(bundle) {
    if (bundle.dataset.ictPriceSync) return;
    bundle.dataset.ictPriceSync = '1';

    var section = bundle.closest('.shopify-section') || document;
    var unit = parseInt(bundle.dataset.unit, 10) || 0;

    function priceEl() {
      return section.querySelector('product-price');
    }

    function priceHost() {
      var p = priceEl();
      return p ? (p.querySelector('.ict-price') || p) : null;
    }

    /* W-177: stock line beside the price, inside a flex row. Idempotent: safe to call after every re-render. */
    function placeStock() {
      var price = priceEl();
      var stock = section.querySelector('.ict-stock');
      if (!price || !stock) return;

      var row = price.parentElement;
      if (!row || !row.classList.contains('ict-price-row')) {
        row = document.createElement('div');
        row.className = 'ict-price-row';
        price.parentNode.insertBefore(row, price);
        row.appendChild(price);
      }
      if (stock.parentElement !== row) row.appendChild(stock);
    }

    function selectedTier() {
      var input = bundle.querySelector('.ict-bundle__input:checked');
      var qty = input ? parseInt(input.value, 10) || 1 : 1;
      var pct = 0;
      if (qty === parseInt(bundle.dataset.t2Qty, 10)) pct = parseInt(bundle.dataset.t2Pct, 10) || 0;
      if (qty === parseInt(bundle.dataset.t3Qty, 10)) pct = parseInt(bundle.dataset.t3Pct, 10) || 0;
      if (qty === parseInt(bundle.dataset.t4Qty, 10)) pct = parseInt(bundle.dataset.t4Pct, 10) || 0;
      return { qty: qty, pct: pct };
    }

    function applyInstalments(total) {
      var amount = section.querySelector('ict-bnpl .ict-bnpl__amount');
      var text = section.querySelector('ict-bnpl .ict-bnpl__text');
      if (!amount || !text) return;
      /* The number of payments is the leading integer of the line itself ("4 interest-free payments ..."). */
      var m = text.textContent.match(/(\d+)/);
      var count = m ? parseInt(m[1], 10) : 4;
      if (!count) return;
      setText(amount, money(Math.round(total / count)));
    }

    function apply() {
      placeStock();
      syncColorOption(section);
      syncCompactOptions(section);
      placeQuantity(section);

      var host = priceHost();
      if (host && unit) {
        var tier = selectedTier();
        var full = unit * tier.qty;
        var total = Math.round((full * (100 - tier.pct)) / 100);

        var html = '<span class="price">' + money(total) + '</span>';
        if (tier.qty > 1 && tier.pct > 0) {
          html += ' <s class="ict-price__compare">' + money(full) + '</s>';
          html += ' <span class="ict-price__qty">· ' + tier.qty + ' units</span>';
        }
        /* The theme's re-render may hand back a fresh host, so the last write is remembered on the host itself. */
        if (host.dataset.ictHtml !== html || !host.querySelector('.price')) {
          host.innerHTML = html;
          host.dataset.ictHtml = html;
        }
        applyInstalments(total);
      }
    }

    var schedule = scheduler(apply);

    /* Tier picked in the bundle block. */
    bundle.addEventListener('change', function (e) {
      if (e.target && e.target.classList.contains('ict-bundle__input')) apply();
    });

    /* Quantity edited directly: the bundle block syncs the radios on input/change/click, so run after it. */
    section.addEventListener('input', function (e) {
      if (e.target && e.target.matches('input[name="quantity"]')) schedule();
    });
    section.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('quantity-selector-component button')) schedule();
    });

    /* Option radio toggled: legend values and selected states follow at once, before the theme's re-render lands. */
    section.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('fieldset.variant-option--buttons')) {
        syncColorOption(section);
        syncCompactOptions(section);
      }
    });

    /* Variant changed: take the new unit price from the event, then re-apply once the theme has re-rendered. */
    document.addEventListener('variant:update', function (e) {
      var r = e && e.detail && (e.detail.resource || e.detail.variant);
      if (r && typeof r.price === 'number') unit = r.price;
      schedule();
      setTimeout(apply, 250);
      setTimeout(apply, 800);
    });

    /* The theme morphs the buy column on re-render; re-apply whenever something else touches it. */
    var details = section.querySelector('.product-details') || section;
    new MutationObserver(schedule).observe(details, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });

    apply();
  }

  /* Products without a bundle block still get the colour squares, the compact options and the quantity beside them. */
  function initColorOnly() {
    if (document.querySelector('ict-bundle')) return;
    document.querySelectorAll('.shopify-section:has(variant-picker)').forEach(function (section) {
      var run = function () {
        syncColorOption(section);
        syncCompactOptions(section);
        placeQuantity(section);
      };
      var schedule = scheduler(run);
      run();
      section.addEventListener('change', function (e) {
        if (e.target && e.target.closest && e.target.closest('fieldset.variant-option--buttons')) {
          syncColorOption(section);
          syncCompactOptions(section);
        }
      });
      new MutationObserver(schedule).observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });
    });
  }

  function boot() {
    placeStrips();
    document.querySelectorAll('ict-bundle').forEach(init);
    initColorOnly();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
